import React, { useState } from 'react'
import { Form, Image, Input, Modal, Spin } from 'antd'
import { useRequest } from 'ahooks'
import { useHistory } from 'react-router'
import Button from '@/components/button/button'
import SgUpload from '@/components/upload/upload'
import { getQiniuImgUrl } from '@/config/qiniu'
import $msg from '@/utils/message'
import { shopStore } from '@/store/shop'
import SettingPage, { SettingGrid, SettingPanel } from '@/views/main/modules/member-manage/member-setting/setting-page'
import { getReceiptPrintConfig, IReceiptPrintConfig, updateReceiptPrintConfig } from '../api'
import { canUploadReceiptImage } from '../setting-rules'

import './ticket-printing.scss'

const TicketPrinting: React.FC = () => {
    const history = useHistory()
    const canUploadImage = canUploadReceiptImage(shopStore.shopInfo?.shop_version)
    const [config, setConfig] = useState<IReceiptPrintConfig>({})
    const [showTextModal, setShowTextModal] = useState(false)
    const [saving, setSaving] = useState(false)
    const [imageSaving, setImageSaving] = useState(false)
    const [loadFailed, setLoadFailed] = useState(false)
    const [form] = Form.useForm<{ attach_text: string }>()

    const { loading, run: load } = useRequest(getReceiptPrintConfig, {
        onSuccess: res => {
            setConfig(res || {})
            setLoadFailed(false)
        },
        onError: (e: any) => {
            setLoadFailed(true)
            $msg('error', e.message || '获取小票打印设置失败')
        },
    })

    const saveText = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const attachText = (values.attach_text || '').replace(/[\r\n\s]+/g, '')
            await updateReceiptPrintConfig({ attach_text: attachText })
            $msg('success', '修改成功')
            setShowTextModal(false)
            load()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '修改失败')
        } finally {
            setSaving(false)
        }
    }

    const uploadImg = (res: any) => {
        const key = res && (res.key || res.hash)
        if (!key) {
            setImageSaving(false)

            return
        }
        setImageSaving(true)
        updateReceiptPrintConfig({ attach_img_url: key })
            .then(() => {
                $msg('success', '修改成功')
                load()
            })
            .catch((e: any) => $msg('error', e.message || '修改失败'))
            .finally(() => setImageSaving(false))
    }

    const removeImg = () => {
        Modal.confirm({
            title: '删除小票附加图片？',
            content: '删除后，新打印的小票将不再显示此图片。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                setImageSaving(true)
                try {
                    await updateReceiptPrintConfig({ attach_img_url: '' })
                    $msg('success', '删除成功')
                    load()
                } catch (e) {
                    $msg('error', e.message || '删除失败')
                } finally {
                    setImageSaving(false)
                }
            },
        })
    }

    return (
        <SettingPage title="小票打印设置" className="store-setting-page ticket-printing">
            <div className="store-setting-page__heading">
                <Button types="text" onClick={() => history.push('/main/setting/cashier')}>
                    返回收银台设置
                </Button>
                <h1 className="store-setting-page__title">小票打印设置</h1>
                <p className="store-setting-page__subtitle">设置小票底部展示的文字与图片。</p>
            </div>
            <Spin spinning={loading}>
                {loadFailed ? (
                    <div className="store-setting-page__error" role="alert">
                        小票设置加载失败。
                        <Button types="text" onClick={() => load()}>
                            重新加载
                        </Button>
                    </div>
                ) : (
                    <SettingGrid className="ticket-printing__layout">
                        <SettingPanel title="附加内容" desc="仅影响后续打印的小票。">
                            <div className="store-setting-page__row">
                                <span className="store-setting-page__label">附加文字</span>
                                <span className="store-setting-page__value ticket-printing__text">
                                    {config.attach_text || '--'}
                                </span>
                                <Button
                                    types="text"
                                    onClick={() => {
                                        form.setFieldsValue({ attach_text: config.attach_text })
                                        setShowTextModal(true)
                                    }}
                                >
                                    修改
                                </Button>
                            </div>

                            <div className="store-setting-page__row">
                                <span className="store-setting-page__label">附加图片</span>
                                <div className="store-setting-page__value">
                                    {config.attach_img_url ? (
                                        <div className="ticket-printing__img-wrap">
                                            <Image
                                                width={40}
                                                height={40}
                                                className="ticket-printing__img"
                                                src={getQiniuImgUrl(config.attach_img_url, 80, 80)}
                                            />
                                        </div>
                                    ) : (
                                        '--'
                                    )}
                                </div>
                                <div className="ticket-printing__image-actions">
                                    {canUploadImage ? (
                                        <SgUpload
                                            className={imageSaving ? 'store-setting-page__upload--disabled' : ''}
                                            accept="image/*"
                                            onLoad={() => setImageSaving(true)}
                                            onError={() => setImageSaving(false)}
                                            onSuccess={uploadImg}
                                        >
                                            <Button types="text" loading={imageSaving} disabled={imageSaving}>
                                                {config.attach_img_url ? '更换' : '添加'}
                                            </Button>
                                        </SgUpload>
                                    ) : (
                                        <Button
                                            types="text"
                                            onClick={() => $msg('error', '升级到专业/旗舰版，可使用附加图片功能')}
                                        >
                                            {config.attach_img_url ? '更换' : '添加'}
                                        </Button>
                                    )}
                                    {config.attach_img_url && (
                                        <Button types="text" disabled={imageSaving} onClick={removeImg}>
                                            删除
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </SettingPanel>
                        <SettingPanel title="小票预览" desc="仅预览底部附加内容，实际小票以打印结果为准。">
                            <div className="ticket-printing__preview">
                                <div className="ticket-printing__preview-heading">小票附加内容</div>
                                <div className="ticket-printing__preview-divider" />
                                <div className="ticket-printing__preview-text">
                                    {config.attach_text || '暂无附加文字'}
                                </div>
                                {config.attach_img_url && (
                                    <Image
                                        className="ticket-printing__preview-img"
                                        src={getQiniuImgUrl(config.attach_img_url, 320, 320)}
                                    />
                                )}
                            </div>
                        </SettingPanel>
                    </SettingGrid>
                )}
            </Spin>

            <Modal
                visible={showTextModal}
                title="小票底部附加文字"
                confirmLoading={saving}
                onOk={saveText}
                onCancel={() => setShowTextModal(false)}
                okText="确认"
                cancelText="取消"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="attach_text" rules={[{ max: 50, message: '最多 50 个字' }]}>
                        <Input.TextArea
                            rows={4}
                            maxLength={50}
                            showCount
                            placeholder="请输入小票底部附加文字（不支持空格、换行）"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </SettingPage>
    )
}

export default TicketPrinting
